import { IHighlightSource, type IDescriptions } from './types'
import { LEGACY_AI2_SOURCE } from './contentSource'
import { config } from './config/generated-config'

export const getCdnUrl = () => {
  return config.VITE_CDN_URL
}

export const getJueApi = () => {
  return config.VITE_JUE_API_BASE_URL
}

export const getDescriptionsApi = () => {
  return config.VITE_DESCRIPTION_API_URL
}

export const getAiGenApi = () => {
  return config.VITE_JUE_API_BASE_URL
}

export const getDescriptionApiURL = (path: string) => {
  const baseUrl = config.VITE_DESCRIPTION_API_URL
  return `${baseUrl}/${path.replace(/^\//, '')}`
}

export const getJamsBackendApi = () => {
  return config.VITE_JAMS_BACKEND_URL
}

export const getJamsFrontendApi = () => {
  return config.VITE_JAMS_FRONTEND_URL
}

export const getAccountNameFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const accountNameFromUrl = urlParams.get('accountName')

  if (accountNameFromUrl && accountNameFromUrl !== '') return accountNameFromUrl

  return ''
}

// export const getAccountIdFromUrl = () => {
//   const urlParams = new URLSearchParams(window.location.search)
//   const accountIdFromUrl = urlParams.get('accountId')

//   if (accountIdFromUrl && accountIdFromUrl !== '') return accountIdFromUrl

//   return ''
// }

export const getAccessTokenFromUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const accessTokenFromUrl = urlParams.get('accessToken')

  if (accessTokenFromUrl && accessTokenFromUrl !== '') return accessTokenFromUrl

  return ''
}

/**
 * Fields the service owns and the Admin only carries.
 *
 * The editor needs to know what the backend decided about a vehicle so it can
 * save the content back without claiming provenance the backend never assigned.
 * Deriving that from the status chip instead would round-trip through a display
 * label, and several distinct sources render as the same label.
 */
const PROVENANCE_FIELDS = [
  'source',
  'descriptionSource',
  'highlightSource',
  'descriptionLanguage',
  'highlightLanguage',
  'descriptionSourceSnapshot',
  'descriptionSourceHash',
  'descriptionTranslatedFrom',
  'descriptionSnapshotSource',
  'highlightSourceSnapshot',
  'highlightSourceHash',
  'highlightTranslatedFrom',
  'highlightSnapshotSource',
  'descriptionLocked',
] as const

export const convertDescriptionResponseToObject = (data: any): IDescriptions => {
  const coerceNode = (node: any) => {
    const provenance: Record<string, unknown> = {}
    for (const field of PROVENANCE_FIELDS) {
      if (node?.[field] !== undefined) provenance[field] = node[field]
    }

    return {
      description: node?.description ?? node?.value ?? '',
      highlight: node?.highlight ?? undefined,
      ...provenance,
    }
  }

  return {
    year: data?.year ?? '',
    make: data?.make ?? '',
    model: data?.model ?? '',
    trim: data?.trim ?? '',
    a5: coerceNode(data?.a5 ?? {}),
    ai2: coerceNode(data?.ai2 ?? {}),
    default: coerceNode(data?.default ?? {}),
  }
}

export const getEmptyDescriptionsTemplate = (): IDescriptions => {
  return {
    year: '',
    make: '',
    model: '',
    trim: '',
    default: {
      description: '',
    },
    ai2: {
      description: '',
    },
    a5: {
      description: '',
    },
  }
}

export const fetchWithTimeout = async (
  url: string,
  options: any,
  timeout: number = 10000,
): Promise<Response> => {
  const controller = new AbortController()
  const config = {
    ...options,
    signal: controller.signal,
  }

  const timeoutId = setTimeout(() => controller.abort(), timeout)

  return await fetch(url, config)
    .then(async (response) => {
      clearTimeout(timeoutId)
      return response
    })
    .catch((error: any) => {
      return error.message
    })
}

/**
 * cleans up description e.g. for displaying in the table
 * @param description
 * @returns
 */
export const toPlainDescription = (description: string): string => {
  if (!description) {
    return ''
  }

  let plainDescription = description

  try {
    if (plainDescription.startsWith('```html') && plainDescription.endsWith('```')) {
      plainDescription = plainDescription.substring(7, plainDescription.length - 3)
    }
    plainDescription = plainDescription.replace(/<[^>]*>?/gm, '')
    plainDescription = plainDescription.trim()
  } catch (err) {
    console.error('Error cleaning up description; returning desc without cleanup', err)
  }

  return plainDescription
}

export const formatAccountName = (name: string): string => {
  return name.replace(/_/g, ' ')
}

/**
 * Provenance older records were given when their content was translated.
 *
 * Translation is an operation, not an origin, so nothing writes it any more and
 * nothing displays it. A record that still carries it has no status here: the
 * account listing does not include the provenance the content had before it was
 * translated, and inventing a plausible one would misreport the vehicle. A
 * status returns on its own the next time something writes the record.
 */
const LEGACY_TRANSLATED_SOURCE = 'ai-translated'

/**
 * Labels are not only shown: the editor reads the label back to recover the
 * source it is working from, so every source the service can persist needs one.
 * A source with no label of its own reads back as unrecognized.
 */
export const getUserFriendlyDescriptionSource = (source: string): string => {
  switch (source) {
    case 'provided':
      return 'From Feed'
    case 'auto-ai':
      return 'AI-Automated'
    case 'ai2':
      return 'AI-Customized'
    case 'user-provided':
      return 'User-Customized'
    case LEGACY_TRANSLATED_SOURCE:
      return ''
    case 'missing':
      return 'Missing'
    default:
      return 'Unknown'
  }
}

export const getUserFriendlyHighlightSource = (source: string): string => {
  switch (source) {
    case IHighlightSource.AutoAi:
      return 'AI-Automated'
    case IHighlightSource.CustomAi:
    case LEGACY_AI2_SOURCE:
      return 'AI-Customized'
    case IHighlightSource.UserProvided:
      return 'User-Customized'
    case LEGACY_TRANSLATED_SOURCE:
      return ''
    case IHighlightSource.Unknown:
      return 'None'
    default:
      return 'None'
  }
}

// Shared status-color mapping used across pages
export type StatusKind = 'description' | 'highlight' | 'generic'

export const getStatusColor = (label: string, kind: StatusKind = 'generic'): string => {
  const normalized = String(label || '').toLowerCase()

  // Highlight-specific: keep existing product palette
  if (kind === 'highlight') {
    if (normalized.includes('ai-automated')) return 'primary'
    if (normalized.includes('ai-customized')) return 'success'
    if (normalized.includes('none')) return 'error'
    return 'default'
  }

  // Description-specific palette
  if (kind === 'description') {
    if (normalized.includes('ai-automated')) return 'primary'
    if (normalized.includes('ai-customized')) return 'success'
    if (normalized.includes('from feed') || normalized.includes('provided')) return 'grey-darken-1'
    if (normalized.includes('missing')) return 'error'
    return 'default'
  }

  // Fallback generic mapping
  if (normalized.includes('custom')) return 'success'
  if (normalized.includes('auto')) return 'primary'
  if (normalized.includes('feed') || normalized.includes('provided')) return 'grey-darken-1'
  if (normalized.includes('none') || normalized.includes('missing')) return 'error'
  if (normalized.includes('unknown')) return 'default'
  return 'default'
}

/**
 * Recover the source behind a status label.
 *
 * `unknown` is a real answer, not a fallback to paper over: it tells the caller
 * the label did not identify a source, and callers must not turn that into a
 * provenance claim.
 */
export const getNormalizedDescriptionSource = (source: string): string => {
  const s = String(source || '').trim().toLowerCase()
  switch (s) {
    case 'from feed':
      return 'provided'
    case 'ai-automated':
      return 'auto-ai'
    case 'ai-customized':
      return 'ai2'
    case 'user-customized':
      return 'user-provided'
    case 'missing':
      return 'missing'
    default:
      return 'unknown'
  }
}

export const redirectToLoginPage = () => {
  const jamsAuthUrl = getJamsFrontendApi()
  const redirectUrl = window.location.href

  if (jamsAuthUrl == null) {
    return
  }

  window.location.href = `${jamsAuthUrl}/?redirect=true&redirectdomain=${redirectUrl}`
}

// Vuetify theme CSS var helper, e.g. primary -> rgb(var(--v-theme-primary))
export const getThemeColorVar = (colorKey: string): string => {
  return `rgb(var(--v-theme-${colorKey}))`
}
